import * as React from 'react'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from './select'

type Option = {
  value: string
  label: string
}

type SimpleSelectProps = Omit<React.ComponentProps<typeof Select>, 'children'> & {
  options: Option[]
  placeholder?: string
  className?: string
  size?: 'sm' | 'default'
  error?: boolean
  /** Goes on the trigger, so a label's htmlFor reaches it. */
  id?: string
}

type SelectWithMoreProps = Omit<React.ComponentProps<typeof Select>, 'children' | 'open' | 'onOpenChange'> & {
  commonOptions: Option[]
  moreOptions: Option[]
  placeholder?: string
  className?: string
  size?: 'sm' | 'default'
  error?: boolean
}

function SelectWithMore({
  commonOptions,
  moreOptions,
  placeholder,
  className,
  size,
  error,
  value,
  onValueChange,
  ...props
}: SelectWithMoreProps) {
  const [expanded, setExpanded] = React.useState(false)
  const [open, setOpen] = React.useState(false)

  // If selected value is from moreOptions, move it into commonOptions
  const visibleCommonOptions = React.useMemo(() => {
    if (value != null && !commonOptions.some((o) => o.value === value)) {
      const selectedOption = moreOptions.find((o) => o.value === value)
      if (selectedOption) {
        return [...commonOptions, selectedOption]
      }
    }
    return commonOptions
  }, [commonOptions, moreOptions, value])

  const visibleMoreOptions = React.useMemo(() => {
    if (value != null && moreOptions.some((o) => o.value === value)) {
      return moreOptions.filter((o) => o.value !== value)
    }
    return moreOptions
  }, [moreOptions, value])

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      setExpanded(false)
    }
  }

  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      open={open}
      onOpenChange={handleOpenChange}
      {...props}
    >
      <SelectTrigger className={className} size={size} aria-invalid={error || undefined}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {visibleCommonOptions.map((option) => (
            <SelectItem value={option.value} key={option.value}>
              {option.label}
            </SelectItem>
          ))}
          {!expanded && visibleMoreOptions.length > 0 && (
            <>
              <SelectSeparator />
              <div
                role="option"
                className="focus:bg-accent focus:text-accent-foreground text-md relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-8 pl-2 outline-hidden select-none md:text-sm"
                onClick={() => setExpanded(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setExpanded(true)
                  }
                }}
                tabIndex={0}
              >
                More...
              </div>
            </>
          )}
          {expanded && visibleMoreOptions.length > 0 && (
            <>
              {visibleMoreOptions.map((option) => (
                <SelectItem value={option.value} key={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

function SimpleSelect({
  options,
  placeholder,
  className,
  size,
  error,
  id,
  value,
  onValueChange,
  ...props
}: SimpleSelectProps) {
  return (
    <Select value={value} onValueChange={onValueChange} {...props}>
      <SelectTrigger id={id} className={className} size={size} aria-invalid={error || undefined}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options.map((option) => (
            <SelectItem value={option.value} key={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

export { SelectWithMore, SimpleSelect }
export type { Option as SelectOption }
